"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Text from "@components/atoms/Text";
import styles from "./VariantWisePrice.module.scss";
import FilterToggle from "@components/molecules/FilterToggle";
import ChatbotSupport from "@components/molecules/ChatbotSupport";
import { CarVariantCard } from "@components/molecules/CarVariantCard";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import {
  extractFields,
  filterConfigs,
  getFilteredVariants,
} from "./VariantWisePriceHelper";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import Button from "@components/atoms/Button";
import { toKebabCase } from "@utils/stringFormatters";
import ImageResponsive from "@components/atoms/ImageResponsive";
import { useCompareToast } from "@hooks/useCompareToast";
import CompareToast from "@components/atoms/CompareToast";
import { PageType } from "@constants/pageTypes";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";

interface VariantWisePriceSectionClientWrapperProps {
  isLoggedIn?: boolean;
  variants: any;
  mmName: string;
  makeName: string;
  modelName: string;
  maxPrice: number;
  minPrice: number;
  isSellable: boolean;
  pageType: PageType;
  carAvailability: string;
  cityName: string;
}

export default function VariantWisePriceSectionClientWrapper({
  isLoggedIn,
  variants,
  mmName,
  makeName,
  modelName,
  maxPrice,
  minPrice,
  isSellable,
  pageType,
  carAvailability,
  cityName,
}: VariantWisePriceSectionClientWrapperProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [compareVariants, setCompareVariants] = useState<any[]>([]);
  const [showMore, setShowMore] = useState(false);
  const [filterDetails, setFilterDetails] = useState<{ [key: string]: any }>(
    {}
  );
  const { toast, showToast, hideToast } = useCompareToast();

  // Memoize expensive calculations
  const fields = useMemo(() => {
    return extractFields(variants?.uniqueSortedVariants || []);
  }, [variants?.uniqueSortedVariants]);

  const filterConfigOptions = useMemo(() => {
    return filterConfigs(fields);
  }, [fields]);

  const filterVariants = useMemo(() => {
    return getFilteredVariants(
      variants?.uniqueSortedVariants || [],
      filterDetails
    );
  }, [variants?.uniqueSortedVariants, filterDetails]);

  const updateUrlWithFilters = useCallback(
    (newFilterDetails: { [key: string]: any }) => {
      const params = new URLSearchParams(searchParams?.toString());

      params.delete("maxPrice");
      params.delete("minPrice");

      const isClearing = Object.keys(newFilterDetails).length === 0;

      if (
        !isClearing &&
        newFilterDetails.priceRange &&
        newFilterDetails.priceRange[0]
      ) {
        const [min, max] = newFilterDetails.priceRange[0].split("-");
        if (min && min !== "0") params.set("minPrice", min);
        if (max) params.set("maxPrice", max);
      }

      const queryString = params.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

      router.replace(newUrl || "", { scroll: false });
    },
    [searchParams, router, pathname]
  );

  const handleFilterChange = useCallback(
    async (filterType: string, value: any) => {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_filter",
          filter_type: filterType,
          selected_value: value,
          car_availability: carAvailability,
          vehicle_make: makeName,
          vehicle_model: modelName,
          city_name: cityName,
        },
      });
    },
    [pageType, isSellable, makeName, modelName]
  );

  const handleViewMoreToggle = useCallback(async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: showMore
          ? "tap_btn_show_less_variants"
          : "tap_btn_view_more_variants",
        car_availability: carAvailability,
        vehicle_make: makeName,
        vehicle_model: modelName,
        city_name: cityName,
      },
    });
    setShowMore(!showMore);
  }, [showMore, pageType, isSellable, makeName, modelName]);

  const handleCompareVariantToggle = useCallback(
    async (car: any, isAlreadyAdded: boolean) => {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: isAlreadyAdded
            ? "tap_btn_remove_from_compare"
            : "tap_btn_add_to_compare",
          variant_name: car.variantName,
          selection: car.variantName,
          car_availability: carAvailability,
          vehicle_make: makeName,
          vehicle_model: modelName,
          city_name: cityName,
        },
      });

      if (isAlreadyAdded) {
        setCompareVariants((prev) =>
          prev.filter((variant) => variant.slug !== car.slug)
        );
      } else {
        if (compareVariants.length < 3) {
          setCompareVariants([
            ...compareVariants,
            {
              ...car,
              modelName: modelName,
              brandName: makeName,
            },
          ]);
        } else {
          showToast("You can only compare up to 3 variants", "error");
        }
      }
    },
    [compareVariants, pageType, isSellable, makeName, modelName, showToast]
  );

  const handleRemoveAllVariants = useCallback(async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_remove_all_variants",
        car_availability: carAvailability,
        vehicle_make: makeName,
        vehicle_model: modelName,
        city_name: cityName,
      },
    });
    setCompareVariants([]);
    showToast("All variants removed from comparison", "info");
  }, [pageType, isSellable, makeName, modelName, showToast]);

  const handleCompareNow = useCallback(async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_compare_now",
        variants_count: compareVariants.length,
        car_availability: carAvailability,
        vehicle_make: makeName,
        vehicle_model: modelName,
        city_name: cityName,
      },
    });

    const variantParams = compareVariants
      ?.map((variant, index) => `variant${index + 1}=${variant?.slug}`)
      .join("&");

    const modelSegment = compareVariants
      ?.map(
        (variant) =>
          `${variant?.brandName
            .toLowerCase()
            .replace(/\s+/g, "-")}-${variant?.modelName
            .toLowerCase()
            .replace(/\s+/g, "-")}`
      )
      ?.join("-vs-");

    const url = `/compare/${modelSegment}?${variantParams}`;
    router.push(url);
  }, [compareVariants, pageType, isSellable, makeName, modelName, router]);

  const handleRemoveVariant = useCallback(
    async (car: any) => {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_remove_variant_from_compare",
          variant_name: car.variantName,
          car_availability: carAvailability,
          vehicle_make: makeName,
          vehicle_model: modelName,
          city_name: cityName,
        },
      });
      setCompareVariants((prev) =>
        prev.filter((variant) => variant.slug !== car.slug)
      );
    },
    [pageType, isSellable, makeName, modelName]
  );

  const [isInitialized, setIsInitialized] = useState(false);
  const [jsEnabled, setJsEnabled] = useState(false);

  // Mark JS as enabled when component mounts
  useEffect(() => {
    setJsEnabled(true);
  }, []);

  useEffect(() => {
    if (isInitialized || !fields.maxPrice || fields.maxPrice.length === 0)
      return;

    const urlMaxPrice = searchParams?.get("maxPrice");
    const urlMinPrice = searchParams?.get("minPrice");

    if (!urlMaxPrice && !urlMinPrice) {
      setIsInitialized(true);
      return;
    }

    const initialFilters: { [key: string]: any } = {};

    if (urlMaxPrice || urlMinPrice) {
      const min = urlMinPrice ? Number(urlMinPrice) : 0;
      const max = urlMaxPrice
        ? Number(urlMaxPrice)
        : fields.maxPrice[0] || maxPrice;
      initialFilters.priceRange = [`${min}-${max}`];
    }

    if (Object.keys(initialFilters).length > 0) {
      setFilterDetails(initialFilters);
    }

    setIsInitialized(true);
  }, [fields.maxPrice, maxPrice, isInitialized, searchParams]);

  // Always render all variants for SEO, but use CSS to hide/show them
  const variantsToShow = filterVariants;

  return (
    <>
      <div className={styles.filterToggleContainer}>
        <FilterToggle
          filterConfigs={filterConfigOptions}
          initialSelectedFilters={filterDetails}
          onApply={async (key, selectedValues) => {
            await handleFilterChange(key, selectedValues);

            const newFilterDetails = {
              ...filterDetails,
              [key]: selectedValues,
            };
            setFilterDetails(newFilterDetails);

            if (key === "priceRange") {
              updateUrlWithFilters(newFilterDetails);
            }
          }}
          onClear={async () => {
            await handleFilterChange("clear", "all");

            setFilterDetails({});
            updateUrlWithFilters({});
            setIsInitialized(true);
          }}
          pageType={pageType}
          brandName={makeName}
          modelName={modelName}
          carAvailability={carAvailability}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.variantsContainer}>
          <div className={`${styles.variantsGrid} ${jsEnabled && !showMore ? styles.hideExtraVariants : ""}`}>
            {variantsToShow?.map((car: any, index: number) => {
              if (car?.[0] === "__typename") return;
              return (
                <div
                  key={index}
                  className={jsEnabled && index >= 4 && !showMore ? styles.hiddenVariant : ""}
                >
                  <CarVariantCard
                  data={car}
                  makeName={makeName}
                  modelName={modelName}
                  isSellable={car.status === "sellable"}
                  isLoggedIn={isLoggedIn}
                  pageType={pageType}
                  carAvailability={carAvailability}
                  cityName={cityName}
                  compareButtonClickHandler={() => {
                    const isAlreadyAdded = compareVariants.some(
                      (variant) => variant.slug === car.slug
                    );
                    handleCompareVariantToggle(car, isAlreadyAdded);
                  }}
                  isAddedToCompare={compareVariants.some(
                    (variant) => variant.slug === car.slug
                  )}
                />
                </div>
              );
            })}
          </div>
          {jsEnabled && filterVariants?.length > 4 && (
            <div className={styles.viewMore}>
              <div
                className={styles.viewMoreLink}
                onClick={handleViewMoreToggle}
              >
                {showMore ? "Show less variants" : `View all variants`}
                <Image
                  src={`${assetLoader()}/icons/Chevron_down.svg`}
                  alt="arrow-down"
                  width={24}
                  height={24}
                  loading="lazy"
                  className={showMore ? styles.arrowIcon : ""}
                />
              </div>
            </div>
          )}
        </div>
        <div className={styles.helperContainer}>
          <ChatbotSupport
            heading="Not sure which variant suits you?"
            subheading="No worries, we'll help you with your question"
            buttonText="Find the right variant"
            buttonLink=""
            icon={`/icons/chatbot.svg`}
            customChatbotStyling={styles.customChatbotStyling}
            chatBotPrompt={`Which variant should I buy in ${makeName} ${modelName}?`}
            carAvailability={carAvailability}
            cityName={cityName}
            modelName={modelName}
            brandName={makeName}
            pageType={pageType}
            section="variant listing"
          />
        </div>
      </div>

      {compareVariants?.length > 0 && (
        <div className={styles.compareVariantsContainer}>
          <div className={styles.compareCarsSection}>
            {compareVariants?.map((car, index) => {
              return (
                <div key={index} className={styles.carSlotImageContainer}>
                  <ImageResponsive
                    filePath={`${toKebabCase(car.brandName)}/${toKebabCase(
                      car.modelName
                    )}`}
                    imageTag={`Hero-Transparent`}
                    alt={`${car.brandName}`}
                    width={100}
                    height={100}
                    loading="lazy"
                    className={styles.carSlotImage}
                    // fill
                    containerClassName={styles.carSlotContainer}
                  />
                  <div className={styles.carSlotText}>
                    <Text level="p" className={styles.carSlotTitle}>
                      {car.modelName}
                    </Text>
                    <Text level="p" className={styles.carSlotSubtitle}>
                      {car.variantName}
                    </Text>
                  </div>
                  <Image
                    src={`${assetLoader()}/icons/Delete.svg`}
                    alt="delete icon"
                    width={24}
                    height={24}
                    loading="lazy"
                    className={styles.deleteIcon}
                    onClick={() => handleRemoveVariant(car)}
                  />
                </div>
              );
            })}
            {Array.from({ length: 3 - compareVariants.length }).map((_, i) => (
              <div key={i} className={styles.carSlot}>
                <Image
                  src={`${assetLoader()}/images/car_slot_icon.png`}
                  alt="slot icon"
                  width={50}
                  height={50}
                  loading="lazy"
                  className={styles.carSlotIcon}
                />
              </div>
            ))}
          </div>
          <div className={styles.compareVariantsButtons}>
            <Button
              variant="wishlist_help"
              className={styles.compareVariantsButton}
              onClick={handleRemoveAllVariants}
            >
              Remove all
            </Button>
            <Button
              variant="wishlist_explore"
              className={styles.compareVariantsButton}
              disabled={compareVariants?.length < 2}
              onClick={handleCompareNow}
            >
              Compare now
            </Button>
          </div>
        </div>
      )}

      <CompareToast toast={toast} onClose={hideToast} />
    </>
  );
}
